import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, CheckCircle2, Target, RefreshCw } from 'lucide-react';
import InlineBehavioralChart from '@/components/recruitment/InlineBehavioralChart';
import AIInsightsLoadingState from '@/components/results/states/AIInsightsLoadingState';
import { sigmaToPercentile } from '@/utils/personalityCalculations';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface BehavioralScores {
  dominance?: number;
  extraversion?: number;
  patience?: number;
  formality?: number;
  dominancePercentile?: number;
  extraversionPercentile?: number;
  patiencePercentile?: number;
  formalityPercentile?: number;
  objectivityPercentile?: number;
}

interface BehaviorProfileData {
  scores: BehavioralScores;
  profileName?: string;
  selectedWordsText?: string[];
}

interface DualBehavioralData {
  selfBehavior?: BehaviorProfileData;
  workBehavior?: BehaviorProfileData;
}

interface AIInsights {
  summary?: string;
  strengths?: string[];
  challenges?: string[];
  recommendations?: string[];
}

interface CompanyFullResultsViewProps {
  behavioralData: any;
  isVisible: boolean;
}

const CompanyFullResultsView: React.FC<CompanyFullResultsViewProps> = ({ 
  behavioralData, 
  isVisible 
}) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [dualData, setDualData] = useState<DualBehavioralData | null>(null);
  const [aiInsights, setAiInsights] = useState<AIInsights | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  // Extract dual behavioral data from submission
  useEffect(() => {
    if (!behavioralData) return;

    const extractedData = extractBehavioralData(behavioralData);
    if (extractedData) {
      setDualData(extractedData);
      generateAIInsights(extractedData);
    }
  }, [behavioralData]);

  const extractBehavioralData = (data: any): DualBehavioralData | null => {
    const scoresObj = data.scores || data;
    
    const extractBehaviorProfile = (profileData: any, defaultName: string): BehaviorProfileData | undefined => {
      if (!profileData) return undefined;
      
      let scores = profileData.scores;
      
      if (!scores || typeof scores !== 'object') {
        if (profileData.dominance !== undefined || profileData.dominancePercentile !== undefined) {
          scores = profileData;
        } else {
          return undefined;
        }
      }
      
      if (scores.dominance !== undefined || scores.dominancePercentile !== undefined) {
        const profileName = profileData.profileName || profileData.profile_name || 
                          scores.profileName || scores.profile_name || defaultName;
        
        return {
          scores: scores,
          profileName: profileName,
          selectedWordsText: profileData.selectedWordsText || profileData.selected_words_text || []
        };
      }
      
      return undefined;
    };
    
    const selfData = scoresObj.selfBehavior;
    const workData = scoresObj.workBehavior;
    
    if (selfData || workData) {
      const result: DualBehavioralData = {};
      
      if (selfData) {
        result.selfBehavior = extractBehaviorProfile(selfData, 'Natural Self');
      }
      if (workData) {
        result.workBehavior = extractBehaviorProfile(workData, 'Work Behavior');
      }
      
      if (result.selfBehavior || result.workBehavior) {
        return result;
      }
    }
    
    // Single assessment fallback
    if (scoresObj.dominance !== undefined || scoresObj.dominancePercentile !== undefined) {
      return {
        workBehavior: {
          scores: scoresObj,
          profileName: data.profile_name || 'Work Behavior',
          selectedWordsText: data.selected_words_text || []
        }
      };
    }
    
    return null;
  };

  const buildBehavioralFactors = (scores: BehavioralScores) => {
    const dominancePercentile = scores.dominancePercentile ?? (scores.dominance ? sigmaToPercentile(scores.dominance) : 50);
    const extraversionPercentile = scores.extraversionPercentile ?? (scores.extraversion ? sigmaToPercentile(scores.extraversion) : 50);
    const patiencePercentile = scores.patiencePercentile ?? (scores.patience ? sigmaToPercentile(scores.patience) : 50);
    const formalityPercentile = scores.formalityPercentile ?? (scores.formality ? sigmaToPercentile(scores.formality) : 50);
    const objectivityPercentile = scores.objectivityPercentile ?? 50;

    return [
      { key: 'dominance', name: 'Drive', percentile: dominancePercentile },
      { key: 'extraversion', name: 'Connection', percentile: extraversionPercentile },
      { key: 'patience', name: 'Task', percentile: patiencePercentile },
      { key: 'formality', name: 'Order', percentile: formalityPercentile },
      { key: 'objectivity', name: 'Style', percentile: objectivityPercentile },
    ];
  };

  const generateAIInsights = async (data: DualBehavioralData) => {
    setLoadingInsights(true);
    try {
      const isDualAssessment = !!(data.selfBehavior && data.workBehavior);
      
      let requestBody: any;
      
      if (isDualAssessment) {
        const selfFactors = buildBehavioralFactors(data.selfBehavior!.scores);
        const workFactors = buildBehavioralFactors(data.workBehavior!.scores);
        
        requestBody = {
          isDualAssessment: true,
          language,
          selfData: {
            behavioralFactors: selfFactors,
            personalityProfile: {
              type: data.selfBehavior!.profileName || 'Natural Self',
              description: ''
            },
            selectedWords: data.selfBehavior!.selectedWordsText || [],
          },
          workData: {
            behavioralFactors: workFactors,
            personalityProfile: {
              type: data.workBehavior!.profileName || 'Work Behavior',
              description: ''
            },
            selectedWords: data.workBehavior!.selectedWordsText || [],
          },
        };
      } else {
        const behaviorData = data.workBehavior || data.selfBehavior;
        if (!behaviorData) return;
        
        requestBody = {
          isDualAssessment: false,
          language,
          behavioralFactors: buildBehavioralFactors(behaviorData.scores),
          selectedWords: behaviorData.selectedWordsText || [],
        };
      }

      const { data: insightsData, error } = await supabase.functions.invoke('generate-personality-insights', {
        body: requestBody
      });

      if (error) {
        console.error('Error generating AI insights:', error);
        return;
      }

      if (insightsData) {
        if (insightsData.selfInsights?.trait_insights) {
          const selfTraits = insightsData.selfInsights.trait_insights;
          const transformedInsights: AIInsights = {
            strengths: selfTraits.map((t: any) => t.strength).filter(Boolean),
            challenges: selfTraits.map((t: any) => t.challenge).filter(Boolean),
          };
          setAiInsights(transformedInsights);
        } else if (insightsData.trait_insights && Array.isArray(insightsData.trait_insights)) {
          const transformedInsights: AIInsights = {
            strengths: insightsData.trait_insights.map((t: any) => t.strength).filter(Boolean),
            challenges: insightsData.trait_insights.map((t: any) => t.challenge).filter(Boolean),
          };
          setAiInsights(transformedInsights);
        } else {
          setAiInsights(insightsData);
        }
      }
    } catch (error) {
      console.error('Error generating AI insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleRegenerateInsights = () => {
    if (dualData) {
      generateAIInsights(dualData);
    }
  };

  const prepareChartScores = (scores: BehavioralScores) => ({
    dominancePercentile: scores.dominancePercentile ?? (scores.dominance ? sigmaToPercentile(scores.dominance) : 50),
    extraversionPercentile: scores.extraversionPercentile ?? (scores.extraversion ? sigmaToPercentile(scores.extraversion) : 50),
    patiencePercentile: scores.patiencePercentile ?? (scores.patience ? sigmaToPercentile(scores.patience) : 50),
    formalityPercentile: scores.formalityPercentile ?? (scores.formality ? sigmaToPercentile(scores.formality) : 50),
    objectivityPercentile: scores.objectivityPercentile ?? 50,
  });

  const hasBehavioral = !!dualData;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className={`mx-auto ${hasBehavioral ? 'max-w-4xl' : 'max-w-3xl'}`}>
          {/* Header with Logo */}
          <div className={`text-center mb-8 md:mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-10 h-10 bg-foreground rounded flex items-center justify-center">
                <span className="text-background font-bold text-lg">Z</span>
              </div>
              <span className="text-xl font-medium text-foreground">Zentrix Insights</span>
            </div>
            
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="bg-primary/10 p-4 rounded-full">
                <CheckCircle2 className="h-12 w-12 text-primary" />
              </div>
            </div>
            
            {/* Professional Confirmation Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('results.companyResults.submittedTitle')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('results.companyResults.submittedSubtitle')}
            </p>
          </div>

          {/* Results Summary */}
          {hasBehavioral && dualData && (
            <div className={`space-y-6 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              
              {/* Behavioral Results */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Self Behavior Profile */}
                {dualData.selfBehavior && (
                  <Card className="border border-border bg-card">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-primary/10 p-2 rounded-lg">
                          <Target className="h-5 w-5 text-primary" />
                        </div>
                        <CardTitle className="text-lg">{t('results.companyResults.selfBehavior')}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {dualData.selfBehavior.profileName && (
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">{t('results.companyResults.profile')}</p>
                          <p className="text-lg font-bold text-foreground">{dualData.selfBehavior.profileName}</p>
                        </div>
                      )}
                      <div className="flex justify-center py-2">
                        <InlineBehavioralChart
                          scores={prepareChartScores(dualData.selfBehavior.scores)}
                          size="card"
                          assessmentVariant="model3"
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center text-xs text-muted-foreground">
                        <div><div className="font-medium text-foreground">D</div><div>{t('results.companyResults.drive')}</div></div>
                        <div><div className="font-medium text-foreground">C</div><div>{t('results.companyResults.connection')}</div></div>
                        <div><div className="font-medium text-foreground">T</div><div>{t('results.companyResults.task')}</div></div>
                        <div><div className="font-medium text-foreground">O</div><div>{t('results.companyResults.order')}</div></div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Work Behavior Profile */}
                {dualData.workBehavior && (
                  <Card className="border border-border bg-card">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-primary/10 p-2 rounded-lg">
                          <Target className="h-5 w-5 text-primary" />
                        </div>
                        <CardTitle className="text-lg">{t('results.companyResults.workBehavior')}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {dualData.workBehavior.profileName && (
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">{t('results.companyResults.profile')}</p>
                          <p className="text-lg font-bold text-foreground">{dualData.workBehavior.profileName}</p>
                        </div>
                      )}
                      <div className="flex justify-center py-2">
                        <InlineBehavioralChart
                          scores={prepareChartScores(dualData.workBehavior.scores)}
                          size="card"
                          assessmentVariant="model3"
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center text-xs text-muted-foreground">
                        <div><div className="font-medium text-foreground">D</div><div>{t('results.companyResults.drive')}</div></div>
                        <div><div className="font-medium text-foreground">C</div><div>{t('results.companyResults.connection')}</div></div>
                        <div><div className="font-medium text-foreground">T</div><div>{t('results.companyResults.task')}</div></div>
                        <div><div className="font-medium text-foreground">O</div><div>{t('results.companyResults.order')}</div></div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* AI Personality Insights */}
              <Card className="border border-border bg-card">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-lg">
                        <Sparkles className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{t('results.companyResults.aiInsightsTitle')}</CardTitle>
                    </div>
                    {aiInsights && !loadingInsights && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRegenerateInsights}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        {t('results.companyResults.refresh')}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingInsights ? (
                    <AIInsightsLoadingState />
                  ) : aiInsights ? (
                    <div className="space-y-6">
                      {aiInsights.summary && (
                        <div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {aiInsights.summary}
                          </p>
                        </div>
                      )}

                      {aiInsights.strengths && aiInsights.strengths.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3">💪 {t('results.companyResults.keyStrengths')}</h4>
                          <ul className="space-y-2">
                            {aiInsights.strengths.map((strength, index) => (
                              <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span>{strength}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {aiInsights.challenges && aiInsights.challenges.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3">🎯 {t('results.companyResults.growthAreas')}</h4>
                          <ul className="space-y-2">
                            {aiInsights.challenges.map((challenge, index) => (
                              <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                                <span className="text-amber-500 mt-1">•</span>
                                <span>{challenge}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3">💡 {t('results.companyResults.recommendations')}</h4>
                          <ul className="space-y-2">
                            {aiInsights.recommendations.map((rec, index) => (
                              <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                                <span className="text-blue-500 mt-1">•</span>
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {t('results.companyResults.insightsError')}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Completion Message */}
              <Card className="border border-border bg-gradient-to-br from-primary/5 to-primary/10">
                <CardContent className="py-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-2 rounded-lg shrink-0">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{t('results.companyResults.whatsNext')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('results.companyResults.whatsNextDesc')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Footer */}
          <div className={`mt-8 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <button 
              onClick={() => navigate('/')} 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('results.companyResults.returnToHome')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyFullResultsView;
